import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../components/AdminNavbar';
import BookingStatusBadge from '../components/BookingStatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../api/axios';

const FILTERS = ['all', 'pending', 'confirmed', 'completed', 'cancelled'];
const STATUSES = ['pending', 'confirmed', 'completed', 'cancelled'];
const REPORT_RANGES = ['all', 'today', 'week', 'month', 'custom'];

const vehicleLabel = (type) => {
  if (type === 'alphard') return 'Toyota Alphard';
  if (type === 'minivan') return 'Mini Van';
  return type || 'Not selected';
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return `$${Number(value).toFixed(2)}`;
};

const toDateInput = (date) => date.toISOString().slice(0, 10);

const getRangeDates = (range, customStart, customEnd) => {
  if (range === 'all') return { start: null, end: null };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start = today;
  let end = today;

  if (range === 'week') {
    start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (range === 'month') {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (range === 'custom') {
    return { start: customStart, end: customEnd };
  }

  return { start: toDateInput(start), end: toDateInput(end) };
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [reportRange, setReportRange] = useState('all');
  const [customStart, setCustomStart] = useState(toDateInput(new Date()));
  const [customEnd, setCustomEnd] = useState(toDateInput(new Date()));

  const [selected, setSelected] = useState(null);
  const [modalStatus, setModalStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [dataError, setDataError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newBookingIds, setNewBookingIds] = useState(new Set());
  const prevBookingIdsRef = useRef(new Set());

  // ── Helpers ────────────────────────────────────────────────────────
  const formatTime = (d) => d
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
    } catch (_) {}
  };

  const notifyNewBookings = (count) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`${count} new booking${count > 1 ? 's' : ''} — Neruda Admin`, {
        body: `${count} new transfer booking${count > 1 ? 's' : ''} received.`,
        icon: `${process.env.PUBLIC_URL}/neruda_logo_hq.webp`,
        tag: 'neruda-new-booking',
      });
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setDataError('');
    try {
      await api.post('/api/admin/bookings?action=auto-complete').catch(() => {});

      const params = filter !== 'all' ? { status: filter } : {};
      const [statsRes, bookingsRes, allRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/bookings', { params }),
        api.get('/api/admin/bookings'),
      ]);
      // Detect new bookings since last refresh (skip on very first load)
      const currentIds = new Set(bookingsRes.data.map(b => b.id));
      if (prevBookingIdsRef.current.size > 0) {
        const newIds = [...currentIds].filter(id => !prevBookingIdsRef.current.has(id));
        if (newIds.length > 0) {
          playBeep();
          notifyNewBookings(newIds.length);
          setNewBookingIds(new Set(newIds));
          setTimeout(() => setNewBookingIds(new Set()), 3000);
        }
      }
      prevBookingIdsRef.current = currentIds;

      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setAllBookings(allRes.data);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 422) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        setDataError('Failed to load bookings. Check your connection and refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Browser tab title shows pending count
  useEffect(() => {
    if (stats) {
      document.title = stats.pending > 0
        ? `(${stats.pending}) Neruda Admin`
        : 'Neruda Admin';
    }
    return () => { document.title = 'Neruda Admin'; };
  }, [stats]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const reportDates = useMemo(
    () => getRangeDates(reportRange, customStart, customEnd),
    [reportRange, customStart, customEnd]
  );

  const reportBookings = useMemo(() => {
    const { start, end } = reportDates;
    // "All" range or missing dates → include every booking
    if (!start || !end) return allBookings;
    return allBookings.filter((b) => b.pickup_date >= start && b.pickup_date <= end);
  }, [allBookings, reportDates]);

  const reportTotal = useMemo(
    () => reportBookings.reduce((sum, b) => sum + Number(b.vehicle_price || 0), 0),
    [reportBookings]
  );

  const openModal = (booking) => {
    setSelected(booking);
    setModalStatus(booking.status);
  };

  const closeModal = () => {
    setSelected(null);
    setModalStatus('');
    setSaveError('');
  };

  const saveStatus = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError('');
    try {
      await api.patch(`/api/admin/bookings/${selected.id}`, { status: modalStatus });
      closeModal();
      loadData();
    } catch {
      setSaveError('Failed to update status. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  const statCards = stats
    ? [
        { label: 'Total Bookings', value: stats.total, accent: 'border-brand-ocean' },
        { label: 'Today', value: stats.today, accent: 'border-brand-gold' },
        { label: 'Pending', value: stats.pending, accent: 'border-amber-400' },
        { label: 'Confirmed', value: stats.confirmed, accent: 'border-green-500' },
      ]
    : [];

  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="no-print">
        <AdminNavbar />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 no-print">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-brand-green">
              Dashboard
            </h1>
            <p className="text-brand-ocean text-sm mt-1">
              New bookings stay pending until you confirm payment or manually approve them.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {lastUpdated && (
              <span className="text-brand-ocean text-xs font-mono bg-white border border-brand-sand rounded-lg px-3 py-2 whitespace-nowrap">
                Updated: {formatTime(lastUpdated)}
              </span>
            )}
            <button
              onClick={loadData}
              disabled={loading}
              title="Refresh now"
              className="p-2.5 bg-white border border-brand-sand rounded-lg hover:bg-brand-bg text-brand-ocean transition-colors disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={printReport}
              className="px-5 py-3 bg-brand-green text-white rounded-xl font-bold hover:bg-brand-ocean transition-colors"
            >
              Download PDF Report
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {statCards.map(({ label, value, accent }) => (
              <div key={label} className={`bg-white rounded-xl p-5 border-l-4 ${accent} shadow-sm`}>
                <p className="text-brand-ocean text-sm font-semibold">{label}</p>
                <p className="font-serif text-4xl font-bold text-brand-green mt-1">{value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white border border-brand-sand rounded-2xl p-5 mb-5 shadow-sm">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
            <div>
              <p className="text-brand-green font-bold mb-3">Booking Status</p>
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                      filter === f
                        ? 'bg-brand-green text-white'
                        : 'bg-brand-sand text-brand-ocean hover:bg-brand-green hover:text-white'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-brand-green font-bold mb-3">PDF Period</p>
              <div className="flex flex-wrap gap-2">
                {REPORT_RANGES.map((range) => (
                  <button
                    key={range}
                    onClick={() => setReportRange(range)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                      reportRange === range
                        ? 'bg-brand-gold text-brand-green'
                        : 'bg-brand-bg text-brand-ocean hover:bg-brand-sand'
                    }`}
                  >
                    {range === 'all' ? 'All Time' : range === 'week' ? 'This Week' : range === 'month' ? 'This Month' : range}
                  </button>
                ))}
              </div>
            </div>

            {reportRange === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-brand-ocean uppercase">From</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="block w-full mt-1 px-3 py-2 rounded-lg border border-brand-sand"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-brand-ocean uppercase">To</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="block w-full mt-1 px-3 py-2 rounded-lg border border-brand-sand"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {dataError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-4 mb-5 text-sm font-semibold">
            {dataError}
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-brand-sand overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-brand-green text-white text-left">
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Reference</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Customer</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap hidden md:table-cell">Phone</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Vehicle</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Pax</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap hidden xl:table-cell">Trip</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Amount</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap hidden lg:table-cell">Pickup Date</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap hidden lg:table-cell">Booked On</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-5 py-16 text-center text-brand-ocean">
                        No bookings found.
                      </td>
                    </tr>
                  ) : (
                    bookings.map((b, i) => (
                      <tr
                        key={b.id}
                        onClick={() => openModal(b)}
                        className={`cursor-pointer transition-colors ${
                          newBookingIds.has(b.id)
                            ? 'booking-new-flash'
                            : `hover:bg-brand-bg ${i % 2 === 0 ? 'bg-white' : 'bg-brand-bg/50'}`
                        }`}
                      >
                        <td className="px-5 py-4 font-mono font-semibold text-brand-green whitespace-nowrap">
                          {b.booking_reference}
                        </td>
                        <td className="px-5 py-4 text-brand-green whitespace-nowrap">{b.customer_name}</td>
                        <td className="px-5 py-4 text-brand-ocean whitespace-nowrap hidden md:table-cell">{b.customer_phone}</td>
                        <td className="px-5 py-4 text-brand-green whitespace-nowrap">
                          <span className="font-semibold">{vehicleLabel(b.vehicle_type)}</span>
                          <span className="block text-xs text-brand-ocean">{b.pricing_zone || '-'}</span>
                        </td>
                        <td className="px-5 py-4 text-center font-bold text-brand-green whitespace-nowrap">
                          {b.passengers}
                          <span className="block text-xs font-normal text-brand-ocean">pax</span>
                        </td>
                        <td className="px-5 py-4 hidden xl:table-cell">
                          <span className="block text-xs text-brand-green font-semibold leading-snug max-w-[200px]">
                            {b.pickup_location}
                          </span>
                          <span className="block text-xs text-brand-ocean mt-0.5">↓</span>
                          <span className="block text-xs text-brand-green font-semibold leading-snug max-w-[200px]">
                            {b.dropoff_location}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-brand-gold font-bold whitespace-nowrap">
                          {formatMoney(b.vehicle_price)}
                        </td>
                        <td className="px-5 py-4 text-brand-ocean hidden lg:table-cell whitespace-nowrap">
                          {b.pickup_date}<span className="block text-xs">{b.pickup_time}</span>
                        </td>
                        <td className="px-5 py-4 text-brand-ocean hidden lg:table-cell whitespace-nowrap">
                          {new Date(b.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                          <span className="block text-xs">{new Date(b.created_at).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })}</span>
                        </td>
                        <td className="px-5 py-4">
                          <BookingStatusBadge status={b.status} />
                          {b.payment_method === 'cash_on_arrival' && (
                            <span className="mt-1 block text-xs font-bold text-amber-600 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 w-fit">
                              💵 Cash
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <section className="print-report">
        <h1>Neruda Tours and Safaris — Booking Report</h1>
        <p>
          Period: {reportRange === 'all' ? 'All Time' : `${reportDates.start || '-'} to ${reportDates.end || '-'}`}
          {' '}| Printed: {new Date().toLocaleDateString('en-GB')}
        </p>
        <p>
          Bookings: {reportBookings.length} | Estimated total: {formatMoney(reportTotal)}
        </p>
        <table>
          <thead>
            <tr>
              <th>Reference</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Pickup</th>
              <th>Drop-off</th>
              <th>Pickup Date</th>
              <th>Booked On</th>
              <th>Vehicle</th>
              <th>Pax</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {reportBookings.map((b) => (
              <tr key={b.id}>
                <td>{b.booking_reference}</td>
                <td>{b.customer_name}</td>
                <td>{b.customer_phone}</td>
                <td>{b.pickup_location}</td>
                <td>{b.dropoff_location}</td>
                <td>{b.pickup_date} {b.pickup_time}</td>
                <td>{new Date(b.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(b.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
                <td>{vehicleLabel(b.vehicle_type)}</td>
                <td>{b.passengers}</td>
                <td>{formatMoney(b.vehicle_price)}</td>
                <td>{b.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 no-print" onClick={closeModal}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-bold text-brand-green">Booking Details</h2>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-brand-sand text-brand-ocean text-xl font-bold transition-colors"
              >
                &times;
              </button>
            </div>

            <div className="bg-brand-sand rounded-xl px-5 py-3 mb-6 border-l-4 border-brand-gold flex items-center justify-between gap-3">
              <div>
                <p className="text-brand-ocean text-xs font-bold uppercase tracking-widest">Reference</p>
                <p className="font-serif text-2xl font-bold text-brand-green">{selected.booking_reference}</p>
              </div>
              {selected.payment_method === 'cash_on_arrival' && (
                <span className="text-xs font-bold text-amber-700 bg-amber-100 border border-amber-400 rounded-lg px-3 py-2 shrink-0">
                  💵 Cash on Arrival
                </span>
              )}
            </div>

            <div className="space-y-2.5 mb-6">
              {[
                { label: 'Name', value: selected.customer_name },
                { label: 'Phone', value: selected.customer_phone },
                { label: 'Email', value: selected.customer_email || '-' },
                { label: 'Pickup', value: selected.pickup_location },
                { label: 'Drop-off', value: selected.dropoff_location },
                { label: 'Zone', value: selected.pricing_zone || '-' },
                { label: 'Vehicle', value: vehicleLabel(selected.vehicle_type) },
                { label: 'Passengers', value: selected.passengers },
                { label: 'Amount', value: formatMoney(selected.vehicle_price) },
                { label: 'Date', value: selected.pickup_date },
                { label: 'Time', value: selected.pickup_time },
                { label: 'Requests', value: selected.special_requests || '-' },
                { label: 'Created', value: new Date(selected.created_at).toLocaleString('en-GB') },
              ].map(({ label, value }) => (
                <div key={label} className="flex gap-4 items-start">
                  <span className="text-brand-ocean text-sm font-semibold w-24 shrink-0">{label}</span>
                  <span className="text-brand-green text-sm">{value}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-brand-sand pt-5">
              <label className="block text-sm font-semibold text-brand-green mb-2">Update Status</label>
              <select
                value={modalStatus}
                onChange={(e) => setModalStatus(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-brand-sand bg-brand-bg focus:outline-none focus:border-brand-ocean text-brand-green mb-4"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-brand-ocean mb-4">
                Use Confirmed after payment is received or when you approve the booking manually.
              </p>
              {saveError && (
                <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2.5 rounded-lg mb-3">
                  {saveError}
                </p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={saveStatus}
                  disabled={saving || modalStatus === selected.status}
                  className="flex-1 py-3 bg-brand-green text-white font-bold rounded-xl hover:bg-brand-ocean transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={closeModal}
                  className="flex-1 py-3 border border-brand-sand text-brand-ocean rounded-xl hover:bg-brand-sand transition-colors font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
